import { HAIRSP } from '../consts/characters';
import { OneHourInMillis, OneMinuteInMillis, OneSecondInMillis } from '../consts/time';
import { isValidDate } from '../functions/validation';

/**
 * Left-pads a number to at least 2 digits with `'0'`.
 *
 * @example
 * leftpad2(5)  // '05'
 * leftpad2(12) // '12'
 */
function leftpad2(num: number): string {
  return String(num).padStart(2, '0');
}

/**
 * Formats a duration in milliseconds as `HH:MM:SS`.
 *
 * Returns an empty string for `null`, `undefined`, non-finite, or negative inputs.
 */
export function formatTimeDuration(time?: number | null): string {
  if (time == null || !Number.isFinite(time) || time < 0) {
    return '';
  }
  if (time === 0) {
    return '00:00:00';
  }
  const sec = Math.floor(time / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);

  return `${leftpad2(hour)}:${leftpad2(min % 60)}:${leftpad2(sec % 60)}`;
}

/**
 * Formats a duration in seconds as `HH:MM:SS` and prefixes day-count when needed.
 *
 * Examples: `65 -> 00:01:05`, `90061 -> 1:01:01:01`, `-5 -> -00:00:05`.
 */
export function formatDurationHHMMSS(seconds: number): string {
  const sign = seconds < 0 ? '-' : '';
  const totalSeconds = Math.floor(Math.abs(seconds));
  const sec = totalSeconds % 60;
  const min = Math.floor(totalSeconds / 60) % 60;
  const hour = Math.floor(totalSeconds / 3600) % 24;
  const day = Math.floor(totalSeconds / (24 * 3600));

  return `${sign}${day > 0 ? `${day}:` : ''}${leftpad2(hour)}:${leftpad2(min)}:${leftpad2(sec)}`;
}

/**
 * Formats a gap value for display, including sign and parentheses.
 *
 * Values above 60 seconds are rendered via `formatDurationHHMMSS`, otherwise as seconds.
 */
export function formatReceiveGap(seconds: number): string {
  if (!Number.isFinite(seconds)) return '';

  const roundedSeconds = Math.round(seconds);
  if (Math.abs(roundedSeconds) > 60) {
    const duration = formatDurationHHMMSS(roundedSeconds);
    return roundedSeconds >= 0 ? ` (+${duration})` : ` (${duration})`;
  }

  return roundedSeconds >= 0 ? ` (+${roundedSeconds}s)` : ` (${roundedSeconds}s)`;
}

/**
 * Parses an `HH:MM:SS` duration string into milliseconds.
 *
 * Returns `NaN` if the resulting value would be negative.
 *
 * @example
 * timeDurationToMilliseconds('01:30:00') // 5_400_000
 * timeDurationToMilliseconds('00:00:05') // 5_000
 */
export function timeDurationToMilliseconds(time: string): number {
  const durationInMilliseconds =
    Number(time.split(':')[0]) * OneHourInMillis +
    Number(time.split(':')[1]) * OneMinuteInMillis +
    Number(time.split(':')[2]) * OneSecondInMillis;
  return durationInMilliseconds < 0 ? Number.NaN : durationInMilliseconds;
}

/**
 * Formats a duration in milliseconds into a human-readable form (e.g. `1d 2h 3min 4s`).
 *
 * Returns an empty string for non-finite or negative values, and `0s` for zero.
 * When `roundToMin` is true, output is rounded to the nearest minute, except values below
 * 30 seconds which are still shown as seconds.
 */
export function formatHumanTimeDuration(time: number, roundToMin?: boolean): string {
  if (!Number.isFinite(time) || time < 0) {
    return '';
  }
  if (time === 0) {
    return '0s';
  }
  const totalSeconds = Math.floor(time / 1000);

  if (roundToMin) {
    // Round to nearest minute (shows seconds if < 30s)
    const totalMinutes = Math.round(totalSeconds / 60);

    // If we have less than a minute, show seconds
    if (totalMinutes === 0) {
      return `${totalSeconds}s`;
    }

    const minutesPerDay = 24 * 60;
    const day = Math.floor(totalMinutes / minutesPerDay);
    const hour = Math.floor((totalMinutes % minutesPerDay) / 60);
    const min = totalMinutes % 60;

    return [day > 0 ? `${day}d` : '', hour > 0 ? `${hour}h` : '', min > 0 ? `${min}min` : '']
      .filter(Boolean)
      .join(' ');
  }

  let sec = Math.floor(time / 1000);
  let min = Math.floor(sec / 60);
  let hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  sec = sec % 60;
  min = min % 60;
  hour = hour % 24;

  return [
    day > 0 ? `${day}d` : '',
    hour > 0 ? `${hour}h` : '',
    min > 0 ? `${min}min` : '',
    sec > 0 ? `${sec}s` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Formats a duration in milliseconds into an abbreviated human-readable form.
 *
 * Smaller units are hidden once the duration exceeds a threshold:
 * - seconds are hidden above 3 minutes
 * - minutes are hidden above 2 hours
 * - hours are hidden above 1 day
 *
 * Returns an empty string for non-finite or negative values, and `'0s'` for zero.
 *
 * @example
 * formatHumanTimeDurationShort(0)          // '0s'
 * formatHumanTimeDurationShort(90_000)     // '1m 30s'
 * formatHumanTimeDurationShort(3_661_000)  // '1h 1m'
 * formatHumanTimeDurationShort(86_400_000) // '1d'
 */
export function formatHumanTimeDurationShort(time: number): string {
  if (!Number.isFinite(time) || time < 0) {
    return '';
  }
  if (time === 0) {
    return '0s';
  }
  let sec = Math.floor(time / 1000);
  let min = Math.floor(sec / 60);
  let hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  sec = sec % 60;
  min = min % 60;
  hour = hour % 24;

  return [
    day > 0 ? `${day}d` : '',
    hour > 0 && time < 86400000 ? `${hour}h` : '',
    min > 0 && time < 7200000 ? `${min}m` : '',
    sec > 0 && time < 180000 ? `${sec}s` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Formats a duration in milliseconds as a single value with the largest applicable unit.
 *
 * When the numeric part exceeds `10^maxDigits`, exponential notation is used.
 *
 * @param time - Duration in milliseconds.
 * @param maxDigits - Maximum digits before switching to exponential notation (default `4`).
 *
 * @example
 * formatHumanTimeDurationFixed(5_000)       // '5s'
 * formatHumanTimeDurationFixed(90_000)      // '1m'
 * formatHumanTimeDurationFixed(3_661_000)   // '1h'
 * formatHumanTimeDurationFixed(90_000_000)  // '1d'
 */
export function formatHumanTimeDurationFixed(time: number, maxDigits = 4): string {
  if (!Number.isFinite(time) || time < 0) return '';
  if (time === 0) return '0s';

  let sec = Math.floor(time / 1000);
  let min = Math.floor(sec / 60);
  let hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  sec = sec % 60;
  min = min % 60;
  hour = hour % 24;

  let value: number;
  let unit: string;

  if (day > 0) {
    value = day;
    unit = 'd';
  } else if (hour > 0) {
    value = hour;
    unit = 'h';
  } else if (min > 0) {
    value = min;
    unit = 'm';
  } else {
    value = sec;
    unit = 's';
  }

  // Format the numeric part so that it uses at most maxDigits digits.
  let valueStr: string;
  if (value < 10 ** maxDigits) {
    valueStr = value.toString();
  } else {
    valueStr = value.toExponential(0);
  }

  return valueStr + unit;
}

/**
 * Formats a Unix timestamp (ms) as a local-time string `HH:MM:SS`.
 *
 * Pass `withoutSeconds = true` to get `HH:MM` instead.
 * Returns an empty string for non-finite inputs and `'00:00:00'` for zero.
 *
 * @example
 * formatTime(new Date('2024-01-15T14:05:09').getTime())       // '14:05:09'
 * formatTime(new Date('2024-01-15T14:05:09').getTime(), true) // '14:05'
 */
export function formatTime(time: number, withoutSeconds?: boolean): string {
  if (!Number.isFinite(time)) return '';
  if (time === 0) {
    return '00:00:00';
  }
  const date = new Date(time);

  const min = date.getMinutes();
  const hour = date.getHours();
  const sec = date.getSeconds();

  return `${leftpad2(hour)}:${leftpad2(min)}${withoutSeconds ? '' : `:${leftpad2(sec)}`}`;
}

/**
 * Formats a Unix timestamp (ms) as an ISO date string `YYYY-MM-DD` in local time.
 *
 * @example
 * formatDate(new Date('2024-01-05').getTime()) // '2024-01-05'
 */
export function formatDate(time: number): string {
  const date = new Date(time);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}-${leftpad2(month)}-${leftpad2(day)}`;
}

/**
 * Formats a duration in whole seconds as a human-readable string using hair-space separated units.
 *
 * Returns `'--'` for `null`, `undefined`, `NaN`, or negative values.
 * Zero-valued components are omitted, except when the total is zero (shows `'0\u202Fs'`).
 *
 * @example
 * formatSeconds(3661) // '1\u200Ah 1\u200Am 1\u200As'
 * formatSeconds(60)   // '1\u200Am'
 * formatSeconds(0)    // '0\u200As'
 * formatSeconds(-1)   // '--'
 * formatSeconds(null) // '--'
 */
export function formatSeconds(totalSeconds: number | null | undefined): string {
  if (
    totalSeconds == null ||
    totalSeconds === undefined ||
    Number.isNaN(totalSeconds) ||
    totalSeconds < 0
  ) {
    return '--';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts: Array<string> = [];
  if (hours > 0) {
    parts.push(`${hours}${HAIRSP}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}${HAIRSP}m`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}${HAIRSP}s`);
  }

  return parts.join(' ');
}

/**
 * Formats a Unix timestamp (ms) or ISO date string as `YYYY-MM-DD HH:MM:SS`.
 *
 * @param time - Unix timestamp in milliseconds or an ISO date string.
 * @param withoutSeconds - When `true`, omits the seconds component (`YYYY-MM-DD HH:MM`).
 * @param withoutDate - When `true`, omits the date prefix and returns only the time part.
 *
 * Returns an empty string for `null` or `undefined`.
 *
 * @example
 * formatDateAndTime(new Date('2024-01-15T14:05:09Z').getTime()) // '2024-01-15 14:05:09' (local)
 * formatDateAndTime('2024-01-15T14:05:09Z', true)               // '2024-01-15 14:05'
 * formatDateAndTime('2024-01-15T14:05:09Z', false, true)        // '14:05:09'
 */
export function formatDateAndTime(
  time: number | string | undefined,
  withoutSeconds?: boolean,
  withoutDate?: boolean,
): string {
  if (time == null) return '';

  const timestamp = typeof time === 'string' ? Date.parse(time) : time;
  return `${withoutDate ? '' : `${formatDate(timestamp)} `}${formatTime(timestamp, withoutSeconds)}`;
}

/**
 * Normalises a date value to an ISO 8601 string suitable for JSON serialisation.
 *
 * Accepts a Unix timestamp (ms) or any date string parseable by `new Date()`.
 * Returns `undefined` for `null`, `undefined`, or values that produce an invalid date.
 *
 * @example
 * formatJSONTime('2024-01-15T14:05:09.000Z') // '2024-01-15T14:05:09.000Z'
 * formatJSONTime(1705327509000)              // '2024-01-15T14:05:09.000Z'
 * formatJSONTime(null)                       // undefined
 * formatJSONTime('not-a-date')               // undefined
 */
export function formatJSONTime(time?: string | number | null): string | undefined {
  if (time == null) return undefined;
  const d = new Date(time);
  if (!isValidDate(d)) return undefined;

  return d.toISOString();
}
